import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  green:  { bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.55)",  text: "#86EFAC", dot: "🟢" },
  yellow: { bg: "rgba(255,179,71,0.12)", border: "rgba(255,179,71,0.55)", text: "#FFB347", dot: "🟡" },
  red:    { bg: "rgba(220,38,38,0.14)",  border: "rgba(220,38,38,0.55)",  text: "#FCA5A5", dot: "🔴" },
};

const PreflightModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/preflight`, { credentials: "include" });
      if (r.ok) {
        setData(await r.json());
      } else {
        setError(`Pre-flight failed (HTTP ${r.status}).`);
      }
    } catch (e) {
      setError(`Network error: ${String(e).slice(0, 200)}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) run();
  }, [open]);

  if (!open) return null;

  const overall = data ? COLOR[data.overall] : COLOR.yellow;

  return (
    <div data-testid="preflight-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(8,13,28,0.85)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  zIndex: 9500, padding: 16 }}>
      <div style={{
        background: "#0f172a", border: "1px solid rgba(184,145,90,0.5)",
        borderLeft: `4px solid ${overall.text}`, borderRadius: 10,
        padding: "1.4rem 1.6rem", width: "100%", maxWidth: 640,
        maxHeight: "92vh", overflowY: "auto",
        boxShadow: "0 28px 60px rgba(0,0,0,0.55)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                          color: "#B8915A", fontWeight: 700 }}>
              🔒 Pre-Flight Deploy Check
            </div>
            <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 17, marginTop: 4 }}>
              {loading ? "Running checks…" : data ? data.overall_msg : "Ready to scan."}
            </div>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="preflight-close"
                  style={{ background: "transparent", border: "none", color: "#9CA3AF",
                           fontSize: 24, cursor: "pointer", lineHeight: 1 }}>
            ×
          </button>
        </div>

        {error && (
          <div style={{ background: COLOR.red.bg, border: `1px solid ${COLOR.red.border}`,
                        color: COLOR.red.text, padding: "0.6rem 0.85rem", borderRadius: 6,
                        fontSize: 14, marginTop: 12 }}>
            ⚠ {error}
          </div>
        )}

        {data && (
          <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
            {data.checks.map((c) => {
              const col = COLOR[c.status] || COLOR.yellow;
              return (
                <div key={c.id}
                     data-testid={`preflight-check-${c.id}`}
                     style={{ background: col.bg, border: `1px solid ${col.border}`,
                              borderLeft: `3px solid ${col.text}`,
                              borderRadius: 6, padding: "0.7rem 0.9rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                                gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 14 }}>
                      {col.dot} {c.label}
                    </div>
                  </div>
                  <div style={{ color: "#D1D5DB", fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
                    {c.detail}
                  </div>
                  {c.fix && (
                    <div style={{ color: col.text, fontSize: 12, marginTop: 6,
                                  paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.06)",
                                  fontStyle: "italic" }}>
                      → {c.fix}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
          <div style={{ color: "#6B7280", fontSize: 11, alignSelf: "center" }}>
            {data?.checked_at && `Last scan ${new Date(data.checked_at).toLocaleTimeString()}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose}
                    data-testid="preflight-dismiss"
                    style={{ background: "transparent", color: "#9CA3AF",
                             border: "1px solid #4B5563", padding: "0.55rem 1.1rem",
                             borderRadius: 5, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              Close
            </button>
            <button type="button" onClick={run} disabled={loading}
                    data-testid="preflight-rerun"
                    style={{ background: loading ? "#4B5563" : "#B8915A", color: "#1b2a4e",
                             border: "1px solid #8a6c3e", padding: "0.55rem 1.3rem",
                             borderRadius: 5, fontWeight: 700, fontSize: 14,
                             cursor: loading ? "not-allowed" : "pointer" }}>
              {loading ? "Scanning…" : "↻ Re-Run Check"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreflightModal;
